/**
 * Body editor for the cat step: category-container slot (auto-populates
 * category rows from the container's BOOL children), source-number slot,
 * and one label → range row per category.
 */
import React, { useCallback } from "react"
import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowsRotate,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons"
import { QueryBuilderStore } from "stores/queryBuilderStore"
import { CatStep } from "types/queryBuilder.types"
import { CodebookData } from "types/codebook.types"
import { getBoolChildLabels } from "utils/codebookUtils"
import SingleFieldSlot from "../SingleFieldSlot.component"
import { StepBodyProps } from "./stepBodyProps"

export interface CatStepBodyProps extends StepBodyProps<CatStep> {
  renameStep: QueryBuilderStore["renameTransformStep"]
  setCatSource: QueryBuilderStore["setCatSource"]
  setCatCategories: QueryBuilderStore["setCatCategories"]
  setCatCategoryRange: QueryBuilderStore["setCatCategoryRange"]
  /** Raw codebook tree — used to read a dropped container's BOOL children. */
  codebook: CodebookData | undefined
}

const CatStepBody: React.FC<CatStepBodyProps> = ({
  step,
  renameStep,
  setCatSource,
  setCatCategories,
  setCatCategoryRange,
  codebook,
}) => {
  // Pull the dropped container's existing BOOL children in as category rows,
  // preserving any ranges already entered for labels that still exist.
  const populateCatCategories = useCallback(
    (container: string): void => {
      if (step.type !== "cat") return
      const labels = getBoolChildLabels(codebook, container)
      const existing = new Map(step.categories.map((c) => [c.label, c.range]))
      setCatCategories(
        step.id,
        labels.map((label) => ({ label, range: existing.get(label) ?? "" }))
      )
    },
    [codebook, step, setCatCategories]
  )

  return (
    <>
      <div className="qc-step__field-row">
        <span className="qc-step__field-label">
          Category container:
          <Tooltip
            title="Drag a container (a group of yes/no fields) from the data tree. Its yes/no children become the categories below. It must already exist in the data tree — if you build it with earlier steps, Apply those first."
            placement="top"
            overlayClassName="dataq-tooltip"
          >
            <FontAwesomeIcon
              icon={faCircleInfo}
              className="qc-step__field-info"
            />
          </Tooltip>
        </span>
        <SingleFieldSlot
          value={step.target}
          onChange={(container) => {
            renameStep(step.id, container)
            populateCatCategories(container)
          }}
          placeholder="Drag a category container (a group of yes/no fields)"
        />
      </div>
      <div className="qc-step__field-row">
        <span className="qc-step__field-label">
          Source field (number):
        </span>
        <SingleFieldSlot
          value={step.source}
          onChange={(v) => setCatSource(step.id, v)}
          placeholder="Drag the INT/REAL field to categorise"
        />
      </div>
      <div className="qc-cat__categories">
        <span className="qc-step__field-label">
          Categories
          <Tooltip
            title="Each yes/no field in the container is a category. Set a range for each: 1 (exact), 18-35 (between, inclusive), <18, <=10, >5, >=10, 56+ (56 or more)."
            placement="top"
            overlayClassName="dataq-tooltip"
          >
            <FontAwesomeIcon
              icon={faCircleInfo}
              className="qc-step__field-info"
            />
          </Tooltip>
          {step.target.trim() && (
            <Tooltip title="Re-read categories from the container">
              <button
                type="button"
                className="qc-cat__refresh"
                aria-label="Refresh categories from container"
                onClick={() => populateCatCategories(step.target)}
              >
                <FontAwesomeIcon icon={faArrowsRotate} />
              </button>
            </Tooltip>
          )}
        </span>
        {step.categories.length === 0 ? (
          <p className="qc-cat__empty">
            {step.target.trim()
              ? "This container has no yes/no (BOOL) fields in the data tree yet. Add them (and Apply) first, then refresh."
              : "Drag a category container above to list its yes/no fields here."}
          </p>
        ) : (
          step.categories.map((c, idx) => (
            <div className="qc-cat__row" key={c.label || idx}>
              <span
                className="qc-cat__label qc-mono"
                title={c.label}
              >
                {c.label}
              </span>
              <span className="qc-cat__arrow" aria-hidden>
                →
              </span>
              <input
                className="qc-recodes__name qc-mono qc-cat__range"
                type="text"
                value={c.range}
                onChange={(e) =>
                  setCatCategoryRange(step.id, idx, e.target.value)
                }
                placeholder="Range (e.g. 18-34, <18, 56+)"
                aria-label={`Range for ${c.label}`}
              />
            </div>
          ))
        )}
      </div>
    </>
  )
}

export default CatStepBody
